import React, { useState, useEffect } from 'react';
import {
  Card, Table, DatePicker, Select, Button, Space, Statistic, Row, Col,
  message, Tag, Tabs, Modal, Descriptions, Empty, Typography, Popconfirm
} from 'antd';
import {
  UserOutlined, ProjectOutlined,
  EyeOutlined, FilterOutlined, BarChartOutlined, DeleteOutlined
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import api from '@/services/api';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

interface User {
  id: number;
  real_name: string;
  role: string;
}

interface Project {
  id: number;
  name: string;
}

interface WorkRecord {
  id: number;
  user: User;
  project?: Project;
  record_type: string;
  work_type?: string;
  leave_type?: string;
  business_trip_type?: string;
  start_time: string;
  end_time?: string;
  duration_hours: number;
  description?: string;
  status: string;
  created_at: string;
}

interface DailyOverview {
  target_date: string;
  total_users: number;
  users: Array<{
    user: User;
    records: WorkRecord[];
    summary: {
      check_in_time?: string;
      check_out_time?: string;
      total_work_hours: number;
      project_work_hours: number;
      non_project_work_hours: number;
      leave_hours: number;
      business_trip_hours: number;
      projects_involved: string[];
    };
  }>;
}

interface WorkStatistics {
  date_range: {
    start_date?: string;
    end_date?: string;
  };
  overall: {
    total_records: number;
    unique_users: number;
    unique_projects: number;
  };
  by_user: Array<{
    user_id: number;
    user_name: string;
    total_records: number;
    work_hours: number;
    leave_hours: number;
    project_count: number;
  }>;
  by_project: Array<{
    project_id: number;
    project_name: string;
    work_records: number;
    total_hours: number;
    user_count: number;
  }>;
  daily_trend: Array<{
    work_date: string;
    total_hours: number;
    active_users: number;
  }>;
}

const AdminWorkLogs: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs()
  ]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<number[]>([]);
  const [recordType, setRecordType] = useState<string>('');
  
  // 数据状态
  const [dailyOverview, setDailyOverview] = useState<DailyOverview | null>(null);
  const [workLogs, setWorkLogs] = useState<WorkRecord[]>([]);
  const [statistics, setStatistics] = useState<WorkStatistics | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  
  // 分页状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  
  // 模态框状态
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<WorkRecord | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userDetailModalVisible, setUserDetailModalVisible] = useState(false);
  const [userWorkLogs, setUserWorkLogs] = useState<WorkRecord[]>([]);

  useEffect(() => {
    loadUsers();
    loadProjects();
    loadDailyOverview(); // 不传递日期参数，让后端使用最近有记录的日期
    loadWorkLogs();
    loadStatistics();
  }, []);

  // 添加键盘事件监听
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // 只在日志查看标签页激活时响应键盘事件
      if (event.target instanceof HTMLElement && 
          (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA')) {
        return; // 如果焦点在输入框内，不处理键盘事件
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        navigateDate(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        navigateDate(1);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedDate]);

  const loadUsers = async () => {
    try {
      const response = await api.getUsers();
      setUsers(response || []);
    } catch (error) {
      console.error('加载用户列表失败:', error);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await api.getProjects();
      setProjects(response || []);
    } catch (error) {
      console.error('加载项目列表失败:', error);
    }
  };

  const loadDailyOverview = async (date?: Dayjs) => {
    setLoading(true);
    try {
      let url = `${import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'}/admin/work-logs/daily-overview`;
      
      // 如果提供了日期参数，则使用指定日期；否则让后端使用最近有记录的日期
      if (date) {
        url += `?target_date=${date.format('YYYY-MM-DD')}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDailyOverview(data);
        
        // 如果后端返回了实际使用的日期，更新前端的选中日期
        if (data.target_date) {
          setSelectedDate(dayjs(data.target_date));
        }
      } else {
        throw new Error('Failed to load daily overview');
      }
    } catch (error) {
      message.error('加载每日概览失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadWorkLogs = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: pagination.pageSize.toString(),
        offset: ((page - 1) * pagination.pageSize).toString()
      });
      
      if (selectedUsers.length > 0) {
        selectedUsers.forEach(userId => params.append('user_id', userId.toString()));
      }
      if (selectedProjects.length > 0) {
        params.append('project_id', selectedProjects[0].toString());
      }
      if (recordType) {
        params.append('record_type', recordType);
      }
      if (dateRange) {
        params.append('start_date', dateRange[0].format('YYYY-MM-DD'));
        params.append('end_date', dateRange[1].format('YYYY-MM-DD'));
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'}/admin/work-logs/user-logs?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setWorkLogs(data.records || []);
        setPagination(prev => ({
          ...prev,
          current: page,
          total: data.total_count || 0
        }));
      } else {
        throw new Error('Failed to load work logs');
      }
    } catch (error) {
      message.error('加载工作日志失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const params = new URLSearchParams();
      if (dateRange) {
        params.append('start_date', dateRange[0].format('YYYY-MM-DD'));
        params.append('end_date', dateRange[1].format('YYYY-MM-DD'));
      }
      if (selectedUsers.length > 0) {
        selectedUsers.forEach(userId => params.append('user_ids', userId.toString()));
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'}/admin/work-logs/statistics?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setStatistics(data);
      } else {
        throw new Error('Failed to load statistics');
      }
    } catch (error) {
      message.error('加载统计数据失败');
      console.error(error);
    }
  };

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      setSelectedDate(date);
      loadDailyOverview(date); // 传递日期参数，使用指定日期
    }
  };

  const navigateDate = (direction: number) => {
    const newDate = selectedDate.add(direction, 'day');
    setSelectedDate(newDate);
    loadDailyOverview(newDate);
  };

  const handleRangeChange = (dates: [Dayjs, Dayjs] | null) => {
    if (dates) {
      setDateRange(dates);
    }
  };

  const handleApplyFilter = () => {
    loadWorkLogs(1);
    loadStatistics();
  };

  const handleResetFilter = () => {
    setSelectedUsers([]);
    setSelectedProjects([]);
    setRecordType('');
    setDateRange([dayjs().subtract(7, 'day'), dayjs()]);
  };

  const openRecordDetail = (record: WorkRecord) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const openUserDetail = async (user: User) => {
    setSelectedUser(user);
    setUserDetailModalVisible(true);
    setLoading(true);
    
    try {
      const params = new URLSearchParams();
      params.append('user_id', user.id.toString());
      if (dateRange) {
        params.append('start_date', dateRange[0].format('YYYY-MM-DD'));
        params.append('end_date', dateRange[1].format('YYYY-MM-DD'));
      } else {
        // 默认显示最近30天的记录
        params.append('start_date', dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
        params.append('end_date', dayjs().format('YYYY-MM-DD'));
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'}/admin/work-logs/user-logs?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setUserWorkLogs(data.records || []);
      } else {
        throw new Error('Failed to load user work logs');
      }
    } catch (error) {
      message.error('加载用户工作记录失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecord = async (recordId: number) => {
    try {
      await api.adminDeleteWorkRecord(recordId);
      message.success('记录删除成功');
      // 刷新数据
      loadWorkLogs();
      loadDailyOverview();
    } catch (error) {
      message.error('删除记录失败');
      console.error(error);
    }
  };

  const getRecordTypeText = (type: string) => {
    const typeMap: Record<string, string> = {
      check_in: '签到',
      check_out: '签退',
      work: '工作',
      leave: '请假',
      business_trip: '出差'
    };
    return typeMap[type] || type;
  };

  const getRecordTypeColor = (type: string) => {
    const colorMap: Record<string, string> = {
      check_in: 'green',
      check_out: 'red',
      work: 'blue',
      leave: 'orange',
      business_trip: 'purple'
    };
    return colorMap[type] || 'default';
  };

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      active: 'green',
      archived: 'orange',
      approved: 'green',
      pending: 'orange',
      rejected: 'red'
    };
    return colorMap[status] || 'default';
  };

  const workLogColumns: ColumnsType<WorkRecord> = [
    {
      title: '用户',
      dataIndex: ['user', 'real_name'],
      key: 'user',
      width: 120,
      render: (name: string) => (
        <Space>
          <UserOutlined />
          {name}
        </Space>
      ),
    },
    {
      title: '项目',
      dataIndex: ['project', 'name'],
      key: 'project',
      width: 150,
      render: (name: string) => (
        name ? (
          <Space>
            <ProjectOutlined />
            {name}
          </Space>
        ) : '-'
      ),
    },
    {
      title: '记录类型',
      dataIndex: 'record_type',
      key: 'record_type',
      width: 100,
      render: (type: string) => (
        <Tag color={getRecordTypeColor(type)}>
          {getRecordTypeText(type)}
        </Tag>
      ),
    },
    {
      title: '开始时间',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 160,
      render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '结束时间',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 160,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: '时长',
      dataIndex: 'duration_hours',
      key: 'duration_hours',
      width: 80,
      render: (hours: number) => hours ? `${hours.toFixed(1)}h` : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => {
        const getStatusText = (status: string) => {
          const statusMap: Record<string, string> = {
            active: '活跃',
            archived: '已归档',
            approved: '已批准',
            pending: '待审批',
            rejected: '已拒绝'
          };
          return statusMap[status] || status;
        };
        
        return (
          <Tag color={getStatusColor(status)}>
            {getStatusText(status)}
          </Tag>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => openRecordDetail(record)}
          >
            详情
          </Button>
          {(record.record_type === 'leave' || record.record_type === 'business_trip') && (
            <Popconfirm
              title="确认删除"
              description="确定要删除这条请假/出差记录吗？"
              onConfirm={() => handleDeleteRecord(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const renderDailyOverview = () => {
    // 过滤掉admin角色的用户
    const nonAdminUsers = dailyOverview ? 
      dailyOverview.users.filter(userWork => userWork.user.role !== 'admin') : [];
    
    // 计算考勤状态统计
    const getAttendanceStats = () => {
      const noCheckIn: string[] = [];
      const noCheckOut: string[] = [];
      const onLeave: string[] = [];
      const noWorkLog: string[] = [];
      
      nonAdminUsers.forEach(userWork => {
        const userName = userWork.user.real_name;
        const hasWorkRecords = userWork.records.some(record => 
          record.record_type === 'work'
        );
        
        if (!userWork.summary.check_in_time) {
          noCheckIn.push(userName);
        }
        if (!userWork.summary.check_out_time) {
          noCheckOut.push(userName);
        }
        if (userWork.summary.leave_hours > 0) {
          onLeave.push(userName);
        }
        if (!hasWorkRecords && userWork.summary.leave_hours === 0) {
          noWorkLog.push(userName);
        }
      });
      
      return { noCheckIn, noCheckOut, onLeave, noWorkLog };
    };

    const attendanceStats = getAttendanceStats();

    return (
      <div style={{ padding: '16px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        {/* 标题和日期 */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '20px',
          backgroundColor: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '20px' }}>数字出版部-管理员日志查看</h2>
          <div style={{ fontSize: '16px', color: '#666' }}>
            {selectedDate.format('YYYY-MM-DD dddd')}
          </div>
          <div style={{ marginTop: '12px' }}>
            <Space>
              <Button 
                                onClick={() => navigateDate(-1)}
                icon={<span>←</span>}
              >
                前一天
              </Button>
              <DatePicker
                value={selectedDate}
                onChange={handleDateChange}
                              />
              <Button 
                                onClick={() => navigateDate(1)}
                icon={<span>→</span>}
              >
                后一天
              </Button>
            </Space>
          </div>
        </div>

        {/* 日志提交状态 */}
        <div style={{ 
          marginBottom: '16px',
          backgroundColor: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ marginBottom: '8px' }}>
            <Text strong>
              已提交({nonAdminUsers.filter(u => 
                u.records.some(r => r.record_type === 'work')
              ).length}): 
            </Text>
            {nonAdminUsers
              .filter(userWork => 
                userWork.records.some(r => r.record_type === 'work')
              )
              .map((userWork, index) => (
                <Tag key={userWork.user.id} style={{ margin: '2px 4px 2px 0' }}>
                  {userWork.user.real_name}
                </Tag>
              ))
            }
          </div>
          <div>
            <Text strong>
              未提交({nonAdminUsers.filter(u => 
                !u.records.some(r => r.record_type === 'work')
              ).length}): 
            </Text>
            {nonAdminUsers
              .filter(userWork => 
                !userWork.records.some(r => r.record_type === 'work')
              )
              .map((userWork, index) => (
                <Tag key={userWork.user.id} color="volcano" style={{ margin: '2px 4px 2px 0' }}>
                  {userWork.user.real_name}
                </Tag>
              ))
            }
          </div>
        </div>

        {/* 考勤状态 */}
        <div style={{ 
          marginBottom: '20px',
          backgroundColor: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <Text strong style={{ marginBottom: '8px', display: 'block' }}>考勤状态</Text>
          <Row gutter={[32, 8]}>
            <Col span={8}>
              <Text>未打上班卡: </Text>
              {attendanceStats.noCheckIn.map((name, index) => (
                <Tag key={index} color="red" style={{ margin: '1px' }}>
                  {name}
                </Tag>
              ))}
            </Col>
            <Col span={8}>
              <Text>未打下班卡: </Text>
              {attendanceStats.noCheckOut.map((name, index) => (
                <Tag key={index} color="orange" style={{ margin: '1px' }}>
                  {name}
                </Tag>
              ))}
            </Col>
            <Col span={8}>
              <Text>请假人员: </Text>
              {attendanceStats.onLeave.map((name, index) => (
                <Tag key={index} color="purple" style={{ margin: '1px' }}>
                  {name}
                </Tag>
              ))}
            </Col>
          </Row>
        </div>

        {/* 用户工作日志列表 */}
        <div>
          {nonAdminUsers.length > 0 ? (
            nonAdminUsers
              .filter(userWork => userWork.records.some(record => record.record_type === 'work'))
              .map((userWork) => {
                const workRecords = userWork.records.filter(record => record.record_type === 'work');
                
                return (
                  <div
                    key={userWork.user.id}
                    style={{
                      backgroundColor: 'white',
                      marginBottom: '16px',
                      padding: '16px',
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      borderLeft: '4px solid #1890ff'
                    }}
                  >
                    {/* 用户名称 */}
                    <div style={{ 
                      marginBottom: '12px', 
                      fontSize: '16px', 
                      fontWeight: 'bold',
                      color: '#1890ff'
                    }}>
                      {userWork.user.real_name}
                    </div>
                    
                    {/* 工作记录 */}
                    {workRecords.map((record, index) => (
                      <div key={record.id} style={{ marginBottom: index < workRecords.length - 1 ? '12px' : '0' }}>
                        {record.description && (
                          <div style={{ 
                            fontSize: '14px',
                            lineHeight: '1.5',
                            color: '#333'
                          }}>
                            {record.description}
                            {(record.duration_hours || record.project) && (
                              <span style={{ color: '#666' }}>
                                （
                                {record.duration_hours && `${record.duration_hours.toFixed(1)}小时`}
                                {record.duration_hours && record.project && '，'}
                                {record.project && record.project.name}
                                ）
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })
          ) : (
            <div style={{
              backgroundColor: 'white',
              padding: '40px',
              textAlign: 'center',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <Empty 
                description="暂无工作日志数据"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderWorkLogs = () => (
    <Card
      title={
        <Space>
          <FilterOutlined />
          工作日志查询
        </Space>
      }
    >
      {/* 筛选条件 */}
      <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#fafafa', borderRadius: 6 }}>
        <Row gutter={16}>
          <Col span={6}>
            <div style={{ marginBottom: 8 }}>日期范围:</div>
            <RangePicker
              value={dateRange}
              onChange={handleRangeChange}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={6}>
            <div style={{ marginBottom: 8 }}>用户:</div>
            <Select
              mode="multiple"
              placeholder="选择用户"
              value={selectedUsers}
              onChange={setSelectedUsers}
              style={{ width: '100%' }}
            >
              {users.map(user => (
                <Option key={user.id} value={user.id}>
                  {user.real_name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <div style={{ marginBottom: 8 }}>项目:</div>
            <Select
              mode="multiple"
              placeholder="选择项目"
              value={selectedProjects}
              onChange={setSelectedProjects}
              style={{ width: '100%' }}
            >
              {projects.map(project => (
                <Option key={project.id} value={project.id}>
                  {project.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <div style={{ marginBottom: 8 }}>记录类型:</div>
            <Select
              placeholder="选择类型"
              value={recordType}
              onChange={setRecordType}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="work">工作</Option>
              <Option value="check_in">签到</Option>
              <Option value="check_out">签退</Option>
              <Option value="leave">请假</Option>
              <Option value="business_trip">出差</Option>
            </Select>
          </Col>
          <Col span={2}>
            <div style={{ marginBottom: 8 }}>&nbsp;</div>
            <Space>
              <Button type="primary" onClick={handleApplyFilter}>
                查询
              </Button>
              <Button onClick={handleResetFilter}>
                重置
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <Table
        columns={workLogColumns}
        dataSource={workLogs}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        }}
        onChange={(paginationInfo) => {
          loadWorkLogs(paginationInfo.current || 1);
        }}
      />
    </Card>
  );

  const renderStatistics = () => (
    <Card
      title={
        <Space>
          <BarChartOutlined />
          工作统计
        </Space>
      }
      loading={!statistics}
    >
      {statistics && (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Statistic
                title="总记录数"
                value={statistics.overall.total_records}
                suffix="条"
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="参与用户"
                value={statistics.overall.unique_users}
                suffix="人"
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="涉及项目"
                value={statistics.overall.unique_projects}
                suffix="个"
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="日均工时"
                value={statistics.daily_trend.length > 0 
                  ? statistics.daily_trend.reduce((sum, day) => sum + day.total_hours, 0) / statistics.daily_trend.length 
                  : 0}
                precision={1}
                suffix="小时"
              />
            </Col>
          </Row>

          <Tabs 
            defaultActiveKey="user"
            items={[
              {
                key: 'user',
                label: '按用户统计',
                children: (
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {statistics.by_user.map(user => (
                      <Card key={user.user_id} style={{ marginBottom: 8 }}>
                        <Row gutter={16}>
                          <Col span={4}>
                            <strong>{user.user_name}</strong>
                          </Col>
                          <Col span={4}>
                            记录数: {user.total_records}
                          </Col>
                          <Col span={4}>
                            工作时长: {user.work_hours.toFixed(1)}h
                          </Col>
                          <Col span={4}>
                            请假时长: {user.leave_hours.toFixed(1)}h
                          </Col>
                          <Col span={4}>
                            参与项目: {user.project_count}个
                          </Col>
                        </Row>
                      </Card>
                    ))}
                  </div>
                ),
              },
              {
                key: 'project',
                label: '按项目统计',
                children: (
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {statistics.by_project.map(project => (
                      <Card key={project.project_id} style={{ marginBottom: 8 }}>
                        <Row gutter={16}>
                          <Col span={6}>
                            <strong>{project.project_name}</strong>
                          </Col>
                          <Col span={4}>
                            工作记录: {project.work_records}条
                          </Col>
                          <Col span={4}>
                            总工时: {project.total_hours.toFixed(1)}h
                          </Col>
                          <Col span={4}>
                            参与人数: {project.user_count}人
                          </Col>
                        </Row>
                      </Card>
                    ))}
                  </div>
                ),
              },
            ]}
          />
        </>
      )}
    </Card>
  );

  return (
    <div>
      <Tabs 
        defaultActiveKey="daily"
        items={[
          {
            key: 'daily',
            label: '📋 日志查看',
            children: renderDailyOverview(),
          },
          {
            key: 'logs', 
            label: '📊 工作记录',
            children: renderWorkLogs(),
          },
          {
            key: 'statistics',
            label: '📈 数据统计', 
            children: renderStatistics(),
          },
        ]}
      />

      {/* 记录详情模态框 */}
      <Modal
        title="工作记录详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedRecord && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="用户">{selectedRecord.user.real_name}</Descriptions.Item>
            <Descriptions.Item label="项目">
              {selectedRecord.project?.name || '无'}
            </Descriptions.Item>
            <Descriptions.Item label="记录类型">
              <Tag color={getRecordTypeColor(selectedRecord.record_type)}>
                {getRecordTypeText(selectedRecord.record_type)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="开始时间">
              {dayjs(selectedRecord.start_time).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="结束时间">
              {selectedRecord.end_time ? dayjs(selectedRecord.end_time).format('YYYY-MM-DD HH:mm:ss') : '无'}
            </Descriptions.Item>
            <Descriptions.Item label="时长">
              {selectedRecord.duration_hours ? `${selectedRecord.duration_hours.toFixed(2)}小时` : '无'}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={getStatusColor(selectedRecord.status)}>
                {(() => {
                  const statusMap: Record<string, string> = {
                    active: '活跃',
                    archived: '已归档',
                    approved: '已批准',
                    pending: '待审批',
                    rejected: '已拒绝'
                  };
                  return statusMap[selectedRecord.status] || selectedRecord.status;
                })()}
              </Tag>
            </Descriptions.Item>
            {selectedRecord.description && (
              <Descriptions.Item label="描述">
                {selectedRecord.description}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="创建时间">
              {dayjs(selectedRecord.created_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* 用户详细工作日志模态框 */}
      <Modal
        title={selectedUser ? `${selectedUser.real_name} - 工作日志详情` : '工作日志详情'}
        open={userDetailModalVisible}
        onCancel={() => {
          setUserDetailModalVisible(false);
          setSelectedUser(null);
          setUserWorkLogs([]);
        }}
        footer={null}
        width="90%"
        style={{ maxWidth: '1200px' }}
      >
        {selectedUser && (
          <div>
            {/* 用户基本信息 */}
            <Card style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic title="用户姓名" value={selectedUser.real_name} />
                </Col>
                <Col span={6}>
                  <Statistic title="用户角色" value={selectedUser.role} />
                </Col>
                <Col span={6}>
                  <Statistic title="记录总数" value={userWorkLogs.length} />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="总工作时长"
                    value={userWorkLogs
                      .filter(record => record.record_type === 'work')
                      .reduce((sum, record) => sum + (record.duration_hours || 0), 0)
                      .toFixed(1)
                    }
                    suffix="小时"
                  />
                </Col>
              </Row>
            </Card>

            {/* 工作记录列表 */}
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {userWorkLogs.length > 0 ? (
                userWorkLogs
                  .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime())
                  .map((record) => (
                    <Card
                      key={record.id}
                                            style={{ 
                        marginBottom: 12,
                        borderLeft: record.record_type === 'work' ? '4px solid #52c41a' :
                                    record.record_type === 'leave' ? '4px solid #faad14' :
                                    record.record_type === 'business_trip' ? '4px solid #722ed1' :
                                    '4px solid #1890ff'
                      }}
                      title={
                        <Space>
                          <Tag color={getRecordTypeColor(record.record_type)}>
                            {getRecordTypeText(record.record_type)}
                          </Tag>
                          <Text strong>
                            {dayjs(record.start_time).format('YYYY-MM-DD dddd')}
                          </Text>
                          {record.project?.name && (
                            <Tag color="blue">{record.project.name}</Tag>
                          )}
                        </Space>
                      }
                      extra={
                        <Space>
                          {record.duration_hours && (
                            <Text type="secondary">{record.duration_hours.toFixed(1)}小时</Text>
                          )}
                          <Tag color={getStatusColor(record.status)}>
                            {(() => {
                              const statusMap: Record<string, string> = {
                                active: '活跃',
                                archived: '已归档',
                                approved: '已批准',
                                pending: '待审批',
                                rejected: '已拒绝'
                              };
                              return statusMap[record.status] || record.status;
                            })()}
                          </Tag>
                        </Space>
                      }
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <Text type="secondary">时间段: </Text>
                          <Text strong>
                            {dayjs(record.start_time).format('HH:mm')} - 
                            {record.end_time ? dayjs(record.end_time).format('HH:mm') : '进行中'}
                          </Text>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary">记录状态: </Text>
                          <Tag color={getStatusColor(record.status)}>
                            {(() => {
                              const statusMap: Record<string, string> = {
                                active: '活跃',
                                archived: '已归档',
                                approved: '已批准',
                                pending: '待审批',
                                rejected: '已拒绝'
                              };
                              return statusMap[record.status] || record.status;
                            })()}
                          </Tag>
                        </Col>
                      </Row>
                      
                      {/* 工作描述 */}
                      {record.description && (
                        <div style={{ marginTop: 12 }}>
                          <Text type="secondary">工作内容: </Text>
                          <div style={{
                            marginTop: 8,
                            padding: '12px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '6px',
                            fontSize: '14px',
                            lineHeight: '1.6'
                          }}>
                            {record.description}
                          </div>
                        </div>
                      )}

                      {/* 请假/出差类型 */}
                      {(record.leave_type || record.business_trip_type) && (
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary">
                            {record.record_type === 'leave' ? '请假类型: ' : '出差类型: '}
                          </Text>
                          <Tag color="orange">
                            {record.leave_type || record.business_trip_type}
                          </Tag>
                        </div>
                      )}
                    </Card>
                  ))
              ) : (
                <Empty description="该用户暂无工作记录" />
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminWorkLogs;